/* eslint-disable no-underscore-dangle */
import React, { useContext, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';

import { formatNumberInputValue, restrictNonNumericCharacters } from '../../../../shared/utils';
import { whenSideBySide, assignFormDesignToCSSVars } from '../fields-customise.style';
import { LabelInputWrapper, LabeledButton } from './labeled_input.style';
import SpecialPropsIndicators from '../props-Indicator/special-props';
import { FormContext } from '../../../shared/contexts/form-context';
import { ctrlDefaults, toHex } from '../../../../shared/form-lib';
import { setUserInputOnCtrl } from '../../../form/reducer/form';
import { selectCtrlIdLabelMap } from '../../selectors';
import { controlValidate } from '../../../form/utils';
import { ControlPropType } from '../../ctrl_proptype';
import {
  ValidationWarning,
  MainfieldWrapper,
  SubtextWrapper,
  FieldWrapper,
  LabelWrapper,
  InputWrapper,
  Input,
  Field,
} from '../../common-field-styles';

const LabeledInput = ({ ctrl, formDesign }) => {
  const { inCustomise, inBuilder, currentPageNo } = useContext(FormContext);
  const { userCustomTexts, form } = useSelector((state) => state.form);
  const responseStore = form?.response_store ?? 'formplus';
  const ctrlIdLabelMap = useSelector(selectCtrlIdLabelMap);
  const fieldLabel = ctrlIdLabelMap?.[ctrl.internal_id];
  const dispatch = useDispatch();

  const [newCtrl, setNewCtrl] = useState(ctrl);
  const [inputValue, setInputValue] = useState(ctrl.value ?? '');
  const [ctrlValidationError, setCtrlValidationError] = useState(ctrl._validationError ?? '');

  const buttonStyles = inBuilder
    ? undefined
    : {
        '--btn-color': toHex(formDesign.design.secondary_button_color),
        '--btn-bag': toHex(formDesign.design.secondary_button_background_color),
      };

  useEffect(() => {
    setInputValue(ctrl.value ?? '');
  }, [ctrl.value]);

  useEffect(() => {
    setCtrlValidationError(ctrl._validationError ?? '');
  }, [ctrl._validationError]);

  const saveUpdatedCtrl = useRef(
    debounce((newValue) => {
      dispatch(
        setUserInputOnCtrl({
          newValue,
          property: 'value',
          page: currentPageNo - 1,
          ctrlId: ctrl.internal_id,
        })
      );
    }, 200)
  );

  const handleInputValue = (e) => {
    setInputValue(e.target.value);
    setNewCtrl((prevCtrl) => ({ ...prevCtrl, value: e.target.value }));
  };

  const handleBlur = () => {
    controlValidate({
      setCtrlValidationError,
      userCustomTexts,
      ctrl: newCtrl,
      responseStore
    });
  };

  const handleChange = (e) => {
    handleInputValue(e);
    const newValue = e.target.value;
    if (ctrl.value !== newValue) {
      saveUpdatedCtrl.current(newValue);
    }
  };
  
  const handlePaste = (e) => formatNumberInputValue(e, handleChange);

  return (
    <MainfieldWrapper style={assignFormDesignToCSSVars(inBuilder, formDesign)}>
      <FieldWrapper>
        {ctrlValidationError && (
          <ValidationWarning className="ui bottom pointing basic label red">{ctrlValidationError}</ValidationWarning>
        )}

        <Field className={!inBuilder ? whenSideBySide(ctrl, formDesign) : ''}>
          <LabelWrapper
            notInBuilder={!inBuilder}
            required={ctrl.props.required}
            className={!inBuilder ? whenSideBySide(ctrl, formDesign) : ''}>
            <label htmlFor={`fpl-${ctrl.internal_id}`} dangerouslySetInnerHTML={{ __html: fieldLabel || ctrlDefaults()[ctrl.type] }} />
            <SpecialPropsIndicators ctrl={ctrl} />
          </LabelWrapper>

          <InputWrapper className={!inBuilder ? whenSideBySide(ctrl, formDesign) : ''}>
            <LabelInputWrapper notInBuilder={!inBuilder}>
              {ctrl.props.left && (
                <LabeledButton
                  style={buttonStyles}
                  notInBuilder={!inBuilder}
                  left={!inBuilder && formDesign.design.form_direction !== 'rtl'}
                  right={!inBuilder && formDesign.design.form_direction === 'rtl'}>
                  {ctrl.props.prepended_text}
                </LabeledButton>
              )}

              <Input
                inLabelledInput
                name={ctrl._name}
                value={inputValue}
                onBlur={handleBlur}
                onPaste={handlePaste}
                onChange={handleChange}
                notInBuilder={!inBuilder}
                required={ctrl.props.required}
                id={`fpl-${ctrl.internal_id}`}
                aria-describedby="descriptive text"
                onKeyDown={restrictNonNumericCharacters}
                type={ctrl.props.type === 2 ? 'number' : 'text'}
                inputMode={ctrl.props.type === 2 ? 'numeric' : 'text'}
                pattern={ctrl.props.type === 2 ? '[0-9]+(.[0-9]+)?' : ''}
                disabled={ctrl.props.readonly || inCustomise || inBuilder}
                placeholder={ctrl.props.placeholder || 'Enter your answer here'}
                title={ctrl.props.readonly ? 'This field cannot be edited' : ''}
              />

              {ctrl.props.right && (
                <LabeledButton
                  style={buttonStyles}
                  notInBuilder={!inBuilder}
                  left={!inBuilder && formDesign.design.form_direction === 'rtl'}
                  right={!inBuilder && formDesign.design.form_direction !== 'rtl'}>
                  {ctrl.props.appended_text}
                </LabeledButton>
              )}
            </LabelInputWrapper>

            <SubtextWrapper id="descriptive text" notInBuilder={!inBuilder}>
              {ctrl.props.subtext}
            </SubtextWrapper>
          </InputWrapper>
        </Field>
      </FieldWrapper>
    </MainfieldWrapper>
  );
};

LabeledInput.defaultProps = {
  formDesign: null,
};

LabeledInput.propTypes = {
  ctrl: PropTypes.shape(ControlPropType).isRequired,
  formDesign: PropTypes.shape({
    old: PropTypes.object.isRequired,
    design: PropTypes.object.isRequired,
  }),
};
export default LabeledInput;
